import { memo, useCallback } from "react"
import { useOptionChainOrderClick } from "./OptionChainOrderClickContext"
import { optionTradeDataType } from "../../../typings/options.typings"
import StrikeTooltip from "../../../../components/StrikeTooltip/StrikeTooltip"
import DynamicColumnRender from "./DynamicColumnRender"
import { OptionSecurity } from "../../../typings/options.typings"
import { OptionType } from "../../../../proto/generated/security"
import { useSelector } from "react-redux"
import { selectOptionChainTableView } from "../../../../selectors/common"
import { OptionChainTableView } from "../../fnoHeader/optionsChainSettingsDialog"
import { Typography } from "@mui/material"
import OptionChainOiBar from "./OptionChainOiBar"
import OptionChainOiChangeBar from "./OptionChainOiChangeBar"
import OptionChainOutlookTag from "./OptionChainOutlookTag"
import { openOptionChainPopover } from "./OptionChainPopoverContext"

const getHighlightClassName = (
    selectedOptionChainTableView: OptionChainTableView,
    text: OptionSecurity | undefined,
    side: string
): string => {
    if (selectedOptionChainTableView === OptionChainTableView.CALLS) {
        return 'highlight-calls'
    }
    if (selectedOptionChainTableView === OptionChainTableView.PUTS) {
        return 'highlight-puts'
    }
    if (selectedOptionChainTableView === OptionChainTableView.OTM || selectedOptionChainTableView === OptionChainTableView.ITM) {
        return 'highlight-calls highlight-puts'
    }
    if (text?.security.optionType === OptionType.CE) {
        return 'highlight-calls'
    }
    if (side === 'left') {
        return 'highlight-calls'
    }
    return 'highlight-puts'
}

const getCursorClassName = (columnId: string): string => {
    if (columnId === 'oiChange|outlook') {
        return 'cursor-pointer'
    }
    return ''
}

const ColumnElement = ({
    text,
    columnId,
    side,
    record,
    subColumn,
    maxOiChange,
    type,
}: {
    text: OptionSecurity & {
        oiRank?: number,
        oiRankChange?: number,
        oiChangeScore?: number
    } | undefined,
    columnId: string,
    side: string,
    record: optionTradeDataType,
    subColumn?: string,
    maxOiChange: [number, number],
    type: 'normal' | 'extended',
}) => {
    const handleOptionOrderClick = useOptionChainOrderClick()
    const isOiColumn = columnId === 'openInterest'
    const isOiChangeColumn = columnId === 'oiChange|outlook'
    const selectedOptionChainTableView = useSelector(selectOptionChainTableView)
    const sideMaxOiChange = side === 'left' ? maxOiChange[0] : maxOiChange[1]
    const oiChangeValue = Number(text?.oiChanges?.oiChange?.change ?? 0)
    const oiPopoverId = `${record.key}-oi-${side}`

    const handleClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        if (type === 'extended') {
            event.stopPropagation()
            handleOptionOrderClick(undefined, record.strikeCol.strike, text.security.optionType === OptionType.CE ? 'call' : 'put')
            return
        }
        if (columnId === 'oiChange|outlook') {
            event.stopPropagation()
            openOptionChainPopover(
                oiPopoverId,
                event.currentTarget,
                <StrikeTooltip
                    record={record}
                    roundStrike={true}
                    partial={side === 'left' ? 'call' : 'put'}
                    keyValueType={false}
                />
            )
        }
    }, [columnId, type, handleOptionOrderClick, oiPopoverId, record, side, text?.security?.optionType, record.strikeCol.strike])

    const textAlign = 'text-right'
    const highlightClass = getHighlightClassName(selectedOptionChainTableView, text, side)
    const cursorClass = getCursorClassName(columnId)
    const rowClassName = `${highlightClass} ${cursorClass} relative table-detail ${textAlign} ${side}-row font-feature-num`

    return (
        !text ? <div style={{ fontSize: '14px', height: '32px' }} className={rowClassName}>
            <Typography sx={{ color: (theme) => theme.palette.text.secondary }}
                className="text-right font-feature-num"
                style={{ fontSize: '14px', height: '32px', padding: "0px !important" }}
            >
                -
            </Typography>
        </div> :
            <div
                style={{ fontSize: '14px', height: '32px', cursor: type === 'extended' ? 'pointer' : 'auto' }}
                className={`${rowClassName}${isOiColumn ? ' option-chain-oi-cell' : ''}${isOiChangeColumn ? ' option-chain-oi-change-cell' : ''}`}
                onClick={handleClick}
            >
                {isOiChangeColumn ? (
                    <div className="option-chain-oi-change-row">
                        <div className="option-chain-oi-change-main">
                            <DynamicColumnRender
                                obj={text}
                                columnId={columnId}
                                subColumn={subColumn}
                                maxOiChange={sideMaxOiChange}
                            />
                            {oiChangeValue !== 0 && (
                                <div className="option-chain-oi-bar-row">
                                    <OptionChainOiChangeBar change={oiChangeValue} maxOiChange={sideMaxOiChange} side={side} />
                                </div>
                            )}
                        </div>
                        <div className="option-chain-oi-change-label">
                            <OptionChainOutlookTag obj={text} />
                        </div>
                    </div>
                ) : isOiColumn ? (
                    <div className="option-chain-oi-main">
                        <DynamicColumnRender
                            obj={text}
                            columnId={columnId}
                            subColumn={subColumn}
                            maxOiChange={sideMaxOiChange}
                        />
                        <OptionChainOiBar text={text} side={side} />
                    </div>
                ) : (
                    <DynamicColumnRender
                        obj={text}
                        columnId={columnId}
                        subColumn={subColumn}
                        maxOiChange={sideMaxOiChange}
                    />
                )}
            </div>
    )
}

export default memo(ColumnElement, (prev, next) =>
    prev.columnId === next.columnId &&
    prev.side === next.side &&
    prev.subColumn === next.subColumn &&
    prev.type === next.type &&
    prev.record.key === next.record.key &&
    prev.text?.security?.code === next.text?.security?.code &&
    prev.maxOiChange[0] === next.maxOiChange[0] &&
    prev.maxOiChange[1] === next.maxOiChange[1]
)
